/**
 * Filter Event Date Bar
 * Handles month pill clicks, select changes, and pill navigation for event filtering.
 */

class FilterEventDateBar {
	constructor(container) {
		this.container = container;
		this.pillsWrapper = container.querySelector(
			'.filter-event-date-bar__pills-wrapper'
		);
		this.pillsContainer = container.querySelector(
			'.filter-event-date-bar__pills'
		);
		this.pills = container.querySelectorAll('.filter-event-date-bar__pill');
		this.select = container.querySelector('.filter-event-date-bar__select');
		this.prevNav = container.querySelector(
			'.filter-event-date-bar__nav--prev'
		);
		this.nextNav = container.querySelector(
			'.filter-event-date-bar__nav--next'
		);
		this.datePickerInput = container.querySelector(
			'.filter-date__date-picker-input'
		);

		this.init();
	}

	init() {
		// Pill click handlers
		if (this.pills.length > 0) {
			this.pills.forEach((pill) => {
				pill.addEventListener('click', (e) => this.handlePillClick(e));
			});
		}

		// Select change handler
		if (this.select) {
			this.select.addEventListener('change', (e) =>
				this.handleSelectChange(e)
			);
		}

		// Nav button handlers
		if (this.prevNav) {
			this.prevNav.addEventListener('click', () =>
				this.scrollPills('left')
			);
		}

		if (this.nextNav) {
			this.nextNav.addEventListener('click', () =>
				this.scrollPills('right')
			);
		}

		// Check nav visibility on load and scroll
		if (this.pillsContainer) {
			this.updateNavVisibility();
			this.pillsContainer.addEventListener('scroll', () =>
				this.updateNavVisibility()
			);
			window.addEventListener('resize', () => this.updateNavVisibility());
		}
	}

	handlePillClick(e) {
		const pill = e.currentTarget;
		const value = pill.dataset.value;

		// Update active state
		this.pills.forEach((p) =>
			p.classList.remove('filter-event-date-bar__pill--active')
		);
		pill.classList.add('filter-event-date-bar__pill--active');

		// Sync select
		if (this.select) {
			this.select.value = value;
		}

		this.filterByMonth(value);
	}

	handleSelectChange(e) {
		const value = e.currentTarget.value;

		// Sync pills
		this.pills.forEach((p) => {
			if (p.dataset.value === value) {
				p.classList.add('filter-event-date-bar__pill--active');
			} else {
				p.classList.remove('filter-event-date-bar__pill--active');
			}
		});

		this.filterByMonth(value);
	}

	filterByMonth(monthKey) {
		if (!this.datePickerInput) {
			return;
		}

		if (!monthKey) {
			// Clear the date filter
			this.datePickerInput.value = '';
		} else {
			// Set date range for the selected month
			const [year, month] = monthKey.split('-');
			const firstDay = `${year}-${month}-01`;
			const lastDay = new Date(year, month, 0).getDate();
			const lastDayFormatted = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;

			this.datePickerInput.value = `${firstDay} - ${lastDayFormatted}`;
		}

		// Trigger input event to notify the filters system
		const event = new Event('input', { bubbles: true });
		this.datePickerInput.dispatchEvent(event);
	}

	scrollPills(direction) {
		if (!this.pillsContainer) return;

		const navOffset = 35; // space for the nav arrow
		const pillsArray = Array.from(this.pills);

		// Find the index of the first pill that's at or past the current scroll position
		let currentIndex = 0;
		for (let i = 0; i < pillsArray.length; i++) {
			const pill = pillsArray[i];
			// Check if this pill's left edge is at or past the scroll position (accounting for navOffset)
			if (
				pill.offsetLeft >=
				this.pillsContainer.scrollLeft + navOffset - 5
			) {
				currentIndex = i;
				break;
			}
			currentIndex = i;
		}

		if (direction === 'right') {
			// Move to next pill
			const nextIndex = Math.min(currentIndex + 1, pillsArray.length - 1);
			const targetPill = pillsArray[nextIndex];
			this.pillsContainer.scrollTo({
				left: targetPill.offsetLeft - navOffset,
				behavior: 'smooth',
			});
		} else {
			// Move to previous pill
			const prevIndex = Math.max(currentIndex - 1, 0);
			const targetPill = pillsArray[prevIndex];
			this.pillsContainer.scrollTo({
				left: targetPill.offsetLeft - navOffset,
				behavior: 'smooth',
			});
		}
	}

	updateNavVisibility() {
		if (!this.pillsContainer || !this.prevNav || !this.nextNav) return;

		const { scrollLeft, scrollWidth, clientWidth } = this.pillsContainer;
		const hasOverflow = scrollWidth > clientWidth;
		const hasPrev = scrollLeft > 0;
		const hasNext =
			hasOverflow && scrollLeft < scrollWidth - clientWidth - 1;

		// Show/hide prev button
		if (hasPrev) {
			this.prevNav.classList.add('filter-event-date-bar__nav--visible');
		} else {
			this.prevNav.classList.remove(
				'filter-event-date-bar__nav--visible'
			);
		}

		// Show/hide next button
		if (hasNext) {
			this.nextNav.classList.add('filter-event-date-bar__nav--visible');
		} else {
			this.nextNav.classList.remove(
				'filter-event-date-bar__nav--visible'
			);
		}

		// Add classes to pills wrapper for CSS targeting
		if (this.pillsWrapper) {
			this.pillsWrapper.classList.toggle(
				'filter-event-date-bar__pills-wrapper--has-prev',
				hasPrev
			);
			this.pillsWrapper.classList.toggle(
				'filter-event-date-bar__pills-wrapper--has-next',
				hasNext
			);
		}
	}
}

// Initialize all filter-event-date-bar components
const filterEventDateBars = document.querySelectorAll('.filter-event-date-bar');

if (filterEventDateBars.length > 0) {
	filterEventDateBars.forEach((container) => {
		new FilterEventDateBar(container);
	});
}
